import {isUndefined, partition} from 'lodash';

import {PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {CellsConversationsBoundary, makeConversationBoundaryBuilder} from '../../../../helpers/cellsHelpers';
import {sortConversations} from '../../../../helpers/models/conversationHelpers';
import {ConversationTeamStatusesEnum} from '../../../../models/api/conversationCommonTypes';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {DataGroupModel} from '../../../../models/api/dataGroupModel';
import {CellsConversationsListRange} from '../../../metadata/cellsLists/cellsList/cellsListState';
import {MaybeFetchedAt} from '../../../metadata/cellsLists/cellsListsHelpers';
import {InboxesConversationsParameters} from '../../inboxes/parameters/inboxesParameters';
import {findFirstSelectedId} from '../../inboxes/parameters/inboxesParametersHelpers';
import {CellsContext} from '../context/cellsContext';
import {
  updateInitialMoreStatusesAndLimits,
  updateMoreStatusAndLimit,
} from '../helpers/cellsPanelReducerHelpers';

/*
 * Types.
 */

type DiscrepancyDetectedHandler = (discrepantConversationIds: ReadonlyArray<number>) => void;

/*
 * Constants.
 */

const anyConversationPredicate: PredicateOf<ConversationBaseModel> = () => true;
const visibleConversationPredicate: PredicateOf<ConversationBaseModel> = (conversation) =>
  conversation.teamStatus !== ConversationTeamStatusesEnum.INVISIBLE;

/*
 * Helpers.
 */

export function fetchInitialCellsConversationsFromDataGroup(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  dataGroup: DataGroupModel,
  onDiscrepancyDetected?: DiscrepancyDetectedHandler,
): MaybeFetchedAt<CellsConversationsListRange> {
  const {conversations, limit} = extractCellsConversations(
    cellsContext,
    parameters,
    dataGroup,
    onDiscrepancyDetected,
  );
  const boundaryBuilder = makeConversationBoundaryBuilder(cellsContext, parameters);
  const aroundConversationIndex = findAroundConversationIndex(parameters, conversations);

  const {topBoundary, bottomBoundary} = updateInitialMoreStatusesAndLimits(
    boundaryBuilder,
    conversations,
    aroundConversationIndex,
    limit,
  );

  return {
    ids: conversations.map((c) => c.id),
    topBoundary,
    bottomBoundary,
    fetchedAt: dataGroup.fetchedAt,
  };
}

export function fetchMoreCellsConversationsFromDataGroup(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsConversationsBoundary,
  dataGroup: DataGroupModel,
  onDiscrepancyDetected?: DiscrepancyDetectedHandler,
): MaybeFetchedAt<CellsConversationsListRange> {
  const {conversations, limit} = extractCellsConversations(
    cellsContext,
    parameters,
    dataGroup,
    onDiscrepancyDetected,
  );
  const boundaryBuilder = makeConversationBoundaryBuilder(cellsContext, parameters);
  const statusAndLimit = updateMoreStatusAndLimit(boundaryBuilder, conversations, direction, limit);

  if ('topBoundary' in statusAndLimit) {
    return {
      // Include the reference conversation, so we can have continuous ranges without needing to deal with
      // non-inclusive boundaries (i.e. open intervals).
      ids: [...conversations.map((c) => c.id), referenceBoundary.id],
      topBoundary: statusAndLimit.topBoundary,
      bottomBoundary: referenceBoundary,
      fetchedAt: dataGroup.fetchedAt,
    };
  }

  return {
    ids: [referenceBoundary.id, ...conversations.map((c) => c.id)],
    topBoundary: referenceBoundary,
    bottomBoundary: statusAndLimit.bottomBoundary,
    fetchedAt: dataGroup.fetchedAt,
  };
}

/** Read the conversations from a conversation page and filter and sort them. */
function extractCellsConversations(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  dataGroup: DataGroupModel,
  onDiscrepancyDetected?: DiscrepancyDetectedHandler,
) {
  // Filter out invisible conversations, except when searching.
  const conversationPredicate = parameters.search ? anyConversationPredicate : visibleConversationPredicate;
  const {conversations, limit} = filterCellsConversations(
    dataGroup.conversationSearchResults ?? dataGroup.conversations ?? [],
    dataGroup.limit ?? 0,
    conversationPredicate,
    onDiscrepancyDetected,
  );

  const sortedConversations = sortConversations(cellsContext, parameters, conversations);

  return {
    conversations: sortedConversations,
    limit,
  };
}

function filterCellsConversations(
  conversations: ReadonlyArray<ConversationBaseModel>,
  limit: number,
  predicate: PredicateOf<ConversationBaseModel>,
  onDiscrepancyDetected?: DiscrepancyDetectedHandler,
) {
  const [filteredConversations, discrepantConversations] = partition(conversations, predicate);

  if (discrepantConversations.length > 0 && onDiscrepancyDetected) {
    onDiscrepancyDetected(discrepantConversations.map(({id}) => id));
  }

  return {
    conversations: filteredConversations,
    limit: Math.max(0, limit - discrepantConversations.length),
  };
}

/** Find the index of the around conversation, if any. */
export function findAroundConversationIndex(
  parameters: InboxesConversationsParameters,
  sortedConversations: ReadonlyArray<ConversationBaseModel>,
) {
  const aroundId = findFirstSelectedId(parameters.selection);
  if (isUndefined(aroundId)) {
    return undefined;
  }
  return sortedConversations.findIndex((c) => c.id === aroundId);
}
